import { createPortal } from "react-dom";
import { CADC, USDC } from "@/config/tokens";
import { formatToken } from "@/lib/format";
import { useRecentTxns } from "@/hooks/use-recent-txns";

const TX_BASE = "https://basescan.org/tx/";

type Kind = "open" | "close" | "supply" | "withdraw";

const KIND_COLOR: Record<Kind, string> = {
  open: "var(--accent-jade)",
  supply: "var(--accent-jade)",
  close: "var(--accent-rose)",
  withdraw: "var(--accent-rose)",
};

const KIND_LABEL: Record<Kind, string> = {
  open: "Open",
  close: "Close",
  supply: "Supply",
  withdraw: "Withdraw",
};

const KindBadge = ({ kind }: { kind: Kind }) => (
  <span
    className="font-mono text-[10px] tracking-[0.14em] uppercase"
    style={{ color: KIND_COLOR[kind] }}
  >
    {KIND_LABEL[kind]}
  </span>
);

const VENUE_LABEL: Record<"morpho" | "ajna" | "vault", string> = {
  morpho: "Morpho",
  ajna: "Ajna",
  vault: "Vault",
};

const ModalContent = ({ onClose }: { onClose: () => void }) => {
  const { data: txns, isLoading, error } = useRecentTxns();

  return (
    <>
      {/* backdrop */}
      <div
        className="fixed inset-0 z-40"
        style={{ background: "rgba(0,0,0,0.25)" }}
        onClick={onClose}
      />

      {/* panel — full-screen on mobile, centered card on sm+ */}
      <div
        className="fixed z-50 inset-0 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-lg sm:rounded-[var(--radius-lg)] px-6 py-7 overflow-y-auto"
        style={{ background: "#252525", border: "1px solid var(--line-2)" }}
      >
        <div className="flex items-center justify-between mb-5">
          <span
            className="font-mono text-[10px] tracking-[0.22em] uppercase"
            style={{ color: "var(--ink-4)" }}
          >
            Recent Transactions
          </span>
          <button
            type="button"
            onClick={onClose}
            className="font-mono text-[11px] transition-opacity hover:opacity-60"
            style={{ color: "var(--ink-3)" }}
          >
            ✕
          </button>
        </div>

        {isLoading && (
          <p
            className="font-mono text-[11px] tracking-[0.12em] uppercase text-center py-6"
            style={{ color: "var(--ink-4)" }}
          >
            Loading…
          </p>
        )}

        {error && (
          <p
            className="font-mono text-[11px] tracking-[0.12em] uppercase text-center py-6"
            style={{ color: "var(--accent-rose)" }}
          >
            {(error as Error).message ?? "Failed to load transactions."}
          </p>
        )}

        {!isLoading && !error && (!txns || txns.length === 0) && (
          <p
            className="font-mono text-[11px] tracking-[0.12em] uppercase text-center py-6"
            style={{ color: "var(--ink-4)" }}
          >
            No transactions found.
          </p>
        )}

        {txns && txns.length > 0 && (
          <table className="w-full text-left border-collapse">
            <thead>
              <tr
                className="font-mono text-[9px] tracking-[0.16em] uppercase"
                style={{ color: "var(--ink-4)" }}
              >
                <th className="pb-2 pr-4 font-normal">Type</th>
                <th className="pb-2 pr-4 font-normal">Venue</th>
                <th className="pb-2 pr-4 font-normal">CADC</th>
                <th className="pb-2 pr-4 font-normal">USDC</th>
                <th className="pb-2" />
              </tr>
            </thead>
            <tbody>
              {txns.map((tx) => (
                <tr
                  key={tx.txHash}
                  className="font-mono text-[11px] tabular-nums cursor-pointer transition-colors"
                  style={{
                    borderTop: "1px solid var(--line-1)",
                    color: "var(--ink-2)",
                  }}
                  onClick={() => window.open(`${TX_BASE}${tx.txHash}`, "_blank", "noreferrer")}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = "rgba(255,255,255,0.04)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = ""; }}
                >
                  <td className="py-2.5 pr-4">
                    <KindBadge kind={tx.kind} />
                  </td>
                  <td className="py-2.5 pr-4">{VENUE_LABEL[tx.venue]}</td>
                  <td className="py-2.5 pr-4">
                    {tx.cadc > 0n ? formatToken(tx.cadc, CADC.decimals, 2) : "—"}
                  </td>
                  <td className="py-2.5 pr-4">
                    {formatToken(tx.usdc, USDC.decimals, 2)}
                  </td>
                  <td className="py-2.5" style={{ color: "var(--ink-4)" }}>→</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

export const HistoryModal = ({ onClose }: { onClose: () => void }) =>
  createPortal(<ModalContent onClose={onClose} />, document.body);
